import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { radius } from '@/shared/theme';
import { Icon, type IconName } from './icon';

interface EmptyStateProps {
  icon?: IconName;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  tone?: 'neutral' | 'bad' | 'primary';
  /** Taller padding for full-page states. */
  page?: boolean;
}

/** Centered icon + message used for empty, error, forbidden and not-found states. */
export function EmptyState({
  icon = 'info',
  title,
  description,
  action,
  tone = 'neutral',
  page,
}: EmptyStateProps) {
  return (
    <Box
      role={tone === 'bad' ? 'alert' : undefined}
      sx={{
        py: page ? { xs: 6, sm: 10 } : 4.5,
        px: 2.5,
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1.25,
      }}
    >
      <Box
        sx={(theme) => {
          const color =
            tone === 'bad'
              ? theme.vars.palette.md.bad
              : tone === 'primary'
                ? theme.vars.palette.md.primary
                : theme.vars.palette.md.faint;
          return {
            width: page ? 56 : 44,
            height: page ? 56 : 44,
            borderRadius: radius.card,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color,
            background: `color-mix(in srgb, ${color} 13%, transparent)`,
            mb: 0.5,
          };
        }}
      >
        <Icon name={icon} size={page ? 26 : 22} />
      </Box>
      <Typography variant={page ? 'h2' : 'h4'} component={page ? 'h1' : 'p'}>
        {title}
      </Typography>
      {description && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ maxWidth: 480, textWrap: 'pretty' }}
        >
          {description}
        </Typography>
      )}
      {action && (
        <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
          {action}
        </Box>
      )}
    </Box>
  );
}
